import { useEffect, useState } from 'react';
import Collapsible from 'react-native-collapsible';
import StyleSheet from 'react-native-media-query';
import { useSelector } from 'react-redux';
import { Layout, useTheme } from '@ui-kitten/components';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';

import useAuth from '../../../../Hooks/useAuth';
import { activeAddressSelector } from '../../../../RTK/user/selectors';
import OtpForm from '../../../../Screens/Auth/Otp';
import SigninForm from '../../../../Screens/Auth/SigninForm';
import SignupForm from '../../../../Screens/Auth/SignupForm';
import ThemeColor from '../../../../Theme/colors';
import ThemeStyle from '../../../../Theme/styles';
import BreakPoints from '../../../../Theme/styles/breakpoints';

import SwitchTab from '../../../SwitchTab';

import { ModalManager } from '../hooks/useModalManager';

import { MODALPROMPT } from './config';
import ModalPrompt from './ModalPrompt';

const switchTabData = [
  { value: 'login', label: 'Log In' },
  { value: 'signup', label: 'Sign Up' },
];

const Authentication = ({ modalId }) => {
  const theme = useTheme();
  const [data, setData] = useState<{
    isLogin?: boolean;
    openDeliveryAddressUponClose?: boolean;
    navigation: any;
  }>();

  const activeAddress = useSelector(activeAddressSelector);
  const {
    isOTPResending,
    isOTPSubmitting,
    isSigninForm,
    payload,
    timer,
    onBack,
    onCountdownEnd,
    onSigninSubmit,
    onOTPSubmitSignUp,
    onOTPSubmit,
    onOTPResend,
  } = useAuth();

  const [activeTabindex, setActiveTabIndex] = useState<number>(0);

  const isReservation = window?.location?.pathname?.includes('reservation');

  useEffect(() => {
    if (!lodashIsEmpty(data)) setActiveTabIndex(data?.isLogin ? 0 : 1);
  }, [data]);

  const toggleOption = (val) => {
    const index = lodashFindIndex(switchTabData, val);
    setActiveTabIndex(index);
  };

  const _onXclicked = async () => {
    await ModalManager.hide(modalId);
    if (data?.openDeliveryAddressUponClose && lodashIsEmpty(activeAddress)) {
      ModalManager.show(MODALPROMPT.deliveryAddress);
    }
  };

  return (
    <ModalPrompt
      id={modalId}
      title={
        isSigninForm
          ? activeTabindex === 0
            ? 'Continue with Email'
            : 'Create your account'
          : 'Enter OTP code'
      }
      closeButtonColor={isReservation && theme['color-info-500']}
      closeOnDocumentClick={false}
      dataSet={{ media: ids.mainWrapper }}
      mainContentWrapperStyle={styles.mainWrapper}
      onBeforeShow={setData}
      onCloseButtonClick={_onXclicked}
      hasHeader
      hasHeaderLine
    >
      <Layout style={styles.contentContainer}>
        {isSigninForm && (
          <SwitchTab
            activeColor={isReservation ? theme['color-info-500'] : undefined}
            data={switchTabData}
            value={switchTabData[activeTabindex]}
            onChange={toggleOption}
          />
        )}

        <Collapsible collapsed={activeTabindex === 1}>
          <Layout>
            {isSigninForm ? (
              <SigninForm
                timer={timer}
                uiKittenStatus={isReservation ? 'info' : 'success'}
                onCountdownEnd={onCountdownEnd}
                onSubmit={onSigninSubmit}
              />
            ) : (
              <OtpForm
                color={isReservation && theme['color-info-500']}
                email={payload?.email || ''}
                isResending={isOTPResending}
                isSubmitting={isOTPSubmitting}
                timer={timer}
                uiKittenStatus={isReservation ? 'info' : 'success'}
                onBack={onBack}
                onCountdownEnd={onCountdownEnd}
                onSubmit={onOTPSubmit}
                onResend={onOTPResend}
              />
            )}
          </Layout>
        </Collapsible>

        <Collapsible collapsed={activeTabindex === 0}>
          {isSigninForm ? (
            <SignupForm
              color={isReservation && theme['color-info-500']}
              modalId={modalId}
              timer={timer}
              uiKittenStatus={isReservation ? 'info' : 'success'}
              onCountdownEnd={onCountdownEnd}
              onSignupSubmit={onSigninSubmit}
            />
          ) : (
            <OtpForm
              color={isReservation && theme['color-info-500']}
              email={payload?.email || ''}
              isResending={isOTPResending}
              isSubmitting={isOTPSubmitting}
              timer={timer}
              uiKittenStatus={isReservation ? 'info' : 'success'}
              onBack={onBack}
              onCountdownEnd={onCountdownEnd}
              onSubmit={onOTPSubmitSignUp}
              onResend={onOTPResend}
            />
          )}
        </Collapsible>
      </Layout>
    </ModalPrompt>
  );
};

const { ids, styles }: { ids: any; styles: any } = StyleSheet.create({
  mainWrapper: {
    borderRadius: 5,
    [`@media ${BreakPoints.xs}`]: {
      width: 370,
    },
    [`@media ${BreakPoints.sm}`]: {
      width: 420,
    },
    [`@media ${BreakPoints.md}`]: {
      width: 440,
    },
    [`@media ${BreakPoints.lg}`]: {
      width: 460,
    },
    [`@media ${BreakPoints.xl}`]: {
      width: 480,
    },
    [`@media ${BreakPoints.xxl}`]: {
      width: 500,
    },
  },

  contentContainer: {
    //@ts-ignore:row gap definition is not available react-native-media-query
    rowGap: 10,
    justifyContent: 'center',
  },
  containerSelector: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.flexDirectionRowCenterSpaceBetween,
  },
  layoutBtnSelector: {
    width: '50%',
  },

  enableBtn: {
    backgroundColor: ThemeColor.green,
    flex: 1,
    marginTop: 10,
    borderWidth: 1,
    borderColor: ThemeColor.green,
  },
  disableBtn: {
    backgroundColor: ThemeColor.gray,
    flex: 1,
    marginTop: 10,
    opacity: 0.5,
    elevation: 0,
    borderWidth: 1,
    borderColor: ThemeColor.gray,
  },

  innerBtn: {
    height: 50,
    minWidth: 150,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },

  underline: {
    textDecorationLine: 'underline',
  },
  //@ts-ignore:row gap definition is not available react-native-media-query
  rowSignUpContent: { rowGap: 20 },
  //@ts-ignore:row gap definition is not available react-native-media-query
  colSignupContent: { ...ThemeStyle.flexDirectionRow, columnGap: 20 },
  legalBindingRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    ...ThemeStyle.spacingVerticalMedium,
  },
});

export default Authentication;
